/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {PrimaryButton} from '../../components/buttons/current/PrimaryButton';
import HourChooser from '../../editors/HourChooser';
import {CreateGeofenceCardHeader} from './CreateGeofenceCardHeader';
import {useGeofenceCardStyles} from './geofenceCardStyles';
import {GeofenceRadius, GeofenceRadiusControl} from './GeofenceRadiusControl';

export type VicinitySearchCardProps = {
  onClose: () => void;
  onSearch: () => void;
  isSearchDisabled: boolean;
  search: ReactNode;
  radius: GeofenceRadius;
  hours: number;
  onHoursChange: (hours: number) => void;
};

const useStyles = makeStyles(() => ({
  body : {
    paddingTop : 8
  },
  // the design's field labels sit 8px in from their fields ...
  label : {
    padding : '0 8px'
  },
  // the vicinity card spaces its search label and field slightly wider than
  // the shared card style ...
  searchGroup : {
    gap : 5
  },
  // the radius control spans the card, so its slider runs edge to edge like
  // the Map Layers opacity slider, with 24px between its field and slider ...
  radius : {
    alignSelf : 'stretch',
    width     : '100%',
    padding   : '6.5px 0',
    gap       : 24
  },
  hoursGroup : {
    display       : 'flex',
    flexDirection : 'column',
    gap           : 8
  },
  // the shared HourChooser caps itself at 150px - the design's field spans
  // the card, with its selected value centered ...
  hoursField : {
    '& > div' : {
      maxWidth : 'none'
    },
    '& .MuiFormControl-root, & .MuiAutocomplete-root' : {
      width : '100%'
    },
    '& .MuiInputBase-input' : {
      textAlign : 'center'
    },
    // the shared current-theme Dropdown field pads its input 10px - this card
    // only uses 5px, scoped here so no other Dropdown changes ...
    '& .MuiAutocomplete-root .MuiAutocomplete-inputRoot' : {
      padding : '5px !important'
    }
  },
  actions : {
    padding : '13px 0'
  }
}));

/**
 * Renders the current theme's vicinity search card: its description, the
 * address search, the radius control, the "Display for Past" hours field and
 * a Search action, which is disabled until a point has been placed on the
 * map.
 *
 * ```typescript
 *
 *   import {VicinitySearchCard} from
 *     '@ion/dls/src/maps/geofences/VicinitySearchCard';
 *   ...
 *
 *   <VicinitySearchCard onClose={onClose}
 *     onSearch={onSearch}
 *     isSearchDisabled={!drawingComplete}
 *     search={<SearchAddress mapView={view} hideLabel />}
 *     radius={radius}
 *     hours={8}
 *     onHoursChange={onHoursChange} />
 *
 * ```
 *
 * @param   onClose          Invoked by the close button.
 * @param   onSearch         Invoked by the Search button.
 * @param   isSearchDisabled Disables the Search button when `true`.
 * @param   search           The address search field.
 * @param   radius           The radius control's values.
 * @param   hours            The number of past hours to display.
 * @param   onHoursChange    Invoked with the newly selected number of hours.
 * @returns                  The vicinity search card.
 */
export const VicinitySearchCard = ({
  onClose,
  onSearch,
  isSearchDisabled,
  search,
  radius,
  hours,
  onHoursChange
}: VicinitySearchCardProps) => {
  const cardClasses = useGeofenceCardStyles();
  const classes     = useStyles();

  return (
    <>
      <CreateGeofenceCardHeader title={translate('maps:contextMenu.vicinitySearch')}
        onClose={onClose} />
      <div className={classnames(cardClasses.body, classes.body)}>
        <p className={cardClasses.description}>
          {translate('maps:geofences.vicinityCard.description')}
        </p>
        <div className={classnames(cardClasses.searchGroup, classes.searchGroup)}>
          <span className={classnames(cardClasses.label, classes.label)}>
            {translate('maps:geofences.vicinityCard.searchLabel')}
          </span>
          <div className={cardClasses.searchField}>
            {search}
          </div>
        </div>
        <GeofenceRadiusControl radius={radius} className={classes.radius} />
        <div className={classes.hoursGroup}>
          <span className={classnames(cardClasses.label, classes.label)}>
            {translate('maps:geofences.vicinityCard.displayForPast')}
          </span>
          <div className={classes.hoursField}>
            <HourChooser value={hours} onChange={onHoursChange} />
          </div>
        </div>
        <div className={classes.actions}>
          <PrimaryButton fullWidth
            disabled={isSearchDisabled}
            onClick={onSearch}>
            {translate('maps:geofences.vicinityCard.search')}
          </PrimaryButton>
        </div>
      </div>
    </>
  );
};

export default VicinitySearchCard;
